import {
  useWSSnackbar,
  WSActions,
  WSAvatar,
  WSDropFileInput,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  VaultFileKind,
  VaultFileVisibility
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import { vaultFilesApi } from "@api";
import { queriesWorkLogs } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { useWSMutation } from "../../../../query/helpers";
import { toLocalVaultUploadUrl } from "../../../../v3/utils/toLocalVaultUploadUrl";
import { WS_LINKS } from "../../../../types/wsLinks";
import styles from "./ModalUploadWorkLogAttachment.module.scss";

/**
 * The v3 replacement for the v1 member-files multipart upload: presign
 * (`createVaultFileUpload`) → PUT the bytes at the returned storage URL →
 * register the vault file (`createVaultFile` with the `uploadId`). Returns the
 * new vault-file id, which the caller appends to the work log's
 * `attachmentIds`.
 *
 * Composed by hand rather than added to the registry: the registry is 1:1 with
 * the SDK, and this orchestrates two SDK calls around a byte PUT that is not an
 * SDK call at all.
 *
 * TODO(v3-blocker): the byte PUT fails its CORS preflight in a browser — the
 * vault storage bucket does not yet allow cross-origin PUT from app origins.
 * `toLocalVaultUploadUrl` routes the PUT through the dev-server proxy in
 * `src/setupProxy.js` so the flow can be exercised locally until the bucket
 * policy lands. Shared with the requirements shared-file upload, which carries
 * the full write-up.
 *
 * TODO(v3-verify): v1 granted the counterparty explicit access
 * (`viewerIds: [payeeId, payerId]`). v3 has no such field — `VaultFile.viewers`
 * is read-only/derived — so access is expected to follow from the file being
 * bound to the work log, which already knows both parties. Confirm in QA that
 * the client can open a file the contractor uploaded, and vice versa.
 */
const useMutationUploadVaultFile = () =>
  useWSMutation<string, unknown, File>(async file => {
    const upload = await vaultFilesApi.createVaultFileUpload({
      vaultFileUploadCreate: {
        filename: file.name,
        mimeType: file.type,
        contentLength: file.size
      }
    });

    const uploadResponse = await fetch(
      toLocalVaultUploadUrl(upload.uploadUrl),
      {
        method: "PUT",
        headers: upload.requiredHeaders,
        body: file
      }
    );

    if (!uploadResponse.ok) {
      throw new Error(
        `Failed to upload file (${uploadResponse.status} ${uploadResponse.statusText})`
      );
    }

    const vaultFile = await vaultFilesApi.createVaultFile({
      vaultFileCreate: {
        uploadId: upload.uploadId,
        title: file.name,
        filename: file.name,
        mimeType: file.type,
        kind: VaultFileKind.DOCUMENT,
        visibility: VaultFileVisibility.PRIVATE
      }
    });

    if (!vaultFile.id) {
      throw new Error("Uploaded file was registered without an id");
    }

    return vaultFile.id;
  });

type Props = {
  workLogId: string;
  existingAttachmentIds: string[];
  onClose: (success: boolean) => void;
} & WSElementProps;

export const ModalUploadWorkLogAttachment: React.FC<Props> = ({
  workLogId,
  existingAttachmentIds,
  onClose,
  ...elementProps
}) => {
  const [file, setFile] = useState<File | null>(null);
  const { openSnackbar } = useWSSnackbar();

  const [uploadFile, uploadFileMeta] = useMutationUploadVaultFile();

  const [updateWorkLog, updateWorkLogMeta] =
    queriesWorkLogs.updateWorkLog.useMutation();

  const handleUpload = async () => {
    if (!file) return;

    try {
      const fileId = await uploadFile(file, { throwOnError: true });

      if (fileId) {
        await updateWorkLog(
          {
            workLogId,
            workLogUpdate: {
              attachmentIds: new Set([...existingAttachmentIds, fileId])
            }
          },
          {
            onSuccess: () => {
              openSnackbar({
                icon: { name: "check" },
                message: "Attachment uploaded"
              });
              onClose(true);
            },
            onError: () => {
              openSnackbar({
                message: "Failed to add attachment",
                type: "error"
              });
            },
            throwOnError: true
          }
        );
      }
    } catch {
      openSnackbar({
        message: "Failed to upload file",
        type: "error"
      });
    }
  };

  const isLoading = uploadFileMeta.isLoading || updateWorkLogMeta.isLoading;

  return (
    <WSElement {...elementProps}>
      <WSList gap="M">
        <WSInfoBox size="S" title="Reminder:">
          Your files are encrypted and secured. Keep in mind that file uploads
          are not designed for storing sensitive payment information (such as
          payment card numbers).{" "}
          <a href={WS_LINKS.security} target="_blank" rel="noopener noreferrer">
            Learn more
          </a>{" "}
          about data security.
        </WSInfoBox>

        <WSText.ParagraphSm color="gray500">
          Upload a file to attach to this work log. PDF, images, and documents
          are supported.
        </WSText.ParagraphSm>

        {file ? (
          <WSElement p="M" colorBackground="gray50">
            <WSFlexBox.CenterY wrap="nowrap">
              <WSAvatar.Icon
                icon="paperclip"
                mr="M"
                color="green500"
                colorBackground="green50"
              />
              <WSText.ParagraphSm singleLine className={styles.fileName}>
                {file.name}
              </WSText.ParagraphSm>
              <WSIcon
                block
                color="gray500"
                size="XS"
                mx="M"
                name="exit"
                onClick={() => setFile(null)}
              />
            </WSFlexBox.CenterY>
          </WSElement>
        ) : (
          <WSElement>
            <WSDropFileInput
              name="attachment"
              buttonText="Browse files"
              buttonProps={{
                type: "button",
                icon: "upload"
              }}
              maxSize={100000000}
              onDropAccepted={accepted => {
                setFile(accepted[0]);
              }}
            />
          </WSElement>
        )}

        <ErrorAlert
          context="Other"
          error={uploadFileMeta.error || updateWorkLogMeta.error}
        />

        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              label: "Cancel",
              type: "button",
              onClick: () => onClose(false)
            },
            {
              kind: "Primary",
              label: "Add attachment",
              onClick: handleUpload,
              loading: isLoading,
              disabled: !file
            }
          ]}
        />
      </WSList>
    </WSElement>
  );
};
